<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSessionStore } from '../../stores/sessionStore';
import { useChatStore } from '../../stores/chatStore';
import { useDataViewStore } from '../../stores/dataViewStore';
import { useReferenceUnitStore } from '../../stores/referenceUnitStore';
import { useFindingsStore } from '../../stores/findingsStore';
import { useNarrativesStore } from '../../stores/narrativesStore';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import SearchMethodologySection from './SearchMethodologySection.vue';
import ChatInput from './ChatInput.vue';
import AddToFindingsDialog, { type FindingSubmit } from './AddToFindingsDialog.vue';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';
import FeedbackDialog from '../common/FeedbackDialog.vue';
import type { Chat } from '../../stores/chatStore';
import { thinkingStepForKind } from '../../utils/workflowProgress';

const { t } = useI18n();
const sessionStore = useSessionStore();
const chatStore = useChatStore();
const dataViewStore = useDataViewStore();
const referenceUnitStore = useReferenceUnitStore();
const findingsStore = useFindingsStore();
const narrativesStore = useNarrativesStore();
const chatContainer = ref<HTMLElement | null>(null);

// The references pill shows the *true* reference count — the number the
// references panel renders — which is only known once that chat's references
// have been hydrated (see referenceUnitStore.getReferenceCount). Until then
// the pill shows just its icon, with no number. We intentionally don't show
// the backend's fast approximation, which can disagree with the panel for
// grouped types (docs_for_claude/reactive_orchestration/references-count-accuracy.md).
const referenceCountFor = (chat: Chat): number | undefined =>
    referenceUnitStore.getReferenceCount(chat.RecordId);

// True on the new-dialogue page: no session and no chats yet. The hero
// ChatInput is rendered here; the docked ChatInput (in MatterPage) takes
// over once a session is selected.
const isEmptyState = computed(
    () => chatStore.chats.length === 0 && !sessionStore.isSessionSelected,
);

marked.setOptions({
    breaks: true,
    gfm: true,
});

const scrollToBottom = () => {
    if (chatContainer.value) {
        chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
};

// When a response completes it replaces the in-flight placeholder in place, so
// the message list doesn't grow and the chatCount-driven scroll-to-bottom never
// fires — a long answer ends up clipped below the fold. Instead of jumping to
// the bottom (which hides the start of a tall response), align the top of the
// latest exchange to the top of the viewport so the answer reads from its
// beginning. Falls back to scroll-to-bottom if the elements can't be found.
const SCROLL_TOP_PADDING = 12;
const scrollLatestResponseIntoView = () => {
    const container = chatContainer.value;
    if (!container) return;
    const items = container.querySelectorAll<HTMLElement>('.chat-message-container');
    if (items.length === 0) return;
    // The final item is the agent answer; the one before it is the user's
    // question. Pin the question to the top so the response follows in context;
    // if there's no preceding question, pin the answer itself.
    const target = items[items.length - 2] ?? items[items.length - 1];
    const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const next = container.scrollTop + delta - SCROLL_TOP_PADDING;
    // Never scroll backwards past where we already are at the bottom: clamp to
    // the max scroll so a short response (shorter than the viewport) still ends
    // flush rather than leaving a gap.
    const maxScroll = container.scrollHeight - container.clientHeight;
    container.scrollTo({ top: Math.min(next, maxScroll), behavior: 'smooth' });
};

const loadChats = async () => {
    try {
        selectedResponseId.value = null;
        await chatStore.loadChats();
        await nextTick();
        scrollToBottom();
    } catch {
        // Error is handled in the store
    }
};

onMounted(() => {
    loadChats();
});

watch(
    () => sessionStore.currentSessionId,
    () => {
        if (sessionStore.currentSessionId && !chatStore.sending) {
            loadChats();
        }
    },
);

watch(
    () => chatStore.chatCount,
    async () => {
        await nextTick();
        scrollToBottom();
    },
);

// A response completes when workflowStatus is cleared back to null (set for the
// duration of send + polling in the store). At that point the placeholder has
// been replaced by the real answer in place, so re-scroll to bring the now-tall
// response into view. Only fires on the active -> idle transition (the watcher
// skips the initial null since it has no `immediate`).
watch(
    () => chatStore.workflowStatus !== null,
    async (active, wasActive) => {
        if (wasActive && !active) {
            await nextTick();
            scrollLatestResponseIntoView();
        }
    },
);

// Sanitize the marked output before it lands in v-html. chat.DisplayText
// is LLM-rendered prose that can echo prompt-injected content from
// matter-context files / queries / retrieved chunks; without this any
// `<script>` or event-handler attribute in the markdown would execute.
const renderMarkdown = (text: string | null | undefined) =>
    DOMPurify.sanitize(marked.parse(text ?? '', { async: false }) as string);

const viewDetails = (chat: Chat) => {
    dataViewStore.openDetailsViewer(chat);
};

interface ChatMenuState {
    chat: Chat;
    x: number;
    y: number;
}
const chatContextMenu = ref<ChatMenuState | null>(null);

const onChatContextMenu = (chat: Chat, event: MouseEvent) => {
    if (chat.Trigger !== 'AGENT') return;
    // Hold ctrl/cmd to bypass us and get the native browser menu.
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    chatContextMenu.value = { chat, x: event.clientX, y: event.clientY };
};

const closeChatContextMenu = () => {
    chatContextMenu.value = null;
};

const chatMenuItems = computed<ContextMenuItem[]>(() => {
    const chat = chatContextMenu.value?.chat;
    if (!chat) return [];
    return [
        {
            label: t('chat.viewDebugTrace'),
            icon: 'bug_report',
            disabled: !chat.AgenticWorkflowId,
            onSelect: async () => {
                if (!chat.AgenticWorkflowId) return;
                try {
                    await chatStore.fetchDETDebugTrace(chat.AgenticWorkflowId);
                } catch (err) {
                    console.error('[ChatWindow] Failed to load debug trace:', err);
                }
            },
        },
    ];
});

const isChatSelected = (chat: Chat) =>
    dataViewStore.isDetailsViewerVisible &&
    dataViewStore.selectedChatForDetails?.RecordId === chat.RecordId;

// While a response's references are being viewed, dim every *other* message so
// the selected response (and its references) stands out. The selected message
// keeps full opacity; all its siblings are faded.
const isChatDimmed = (chat: Chat) =>
    dataViewStore.isDetailsViewerVisible &&
    dataViewStore.selectedChatForDetails?.RecordId !== chat.RecordId;

// --- Per-response feedback ----------------------------------------------------
// A completed agent answer gets a thumbs-up/down row below it. Thumbs-up posts
// positive feedback immediately; thumbs-down opens the shared FeedbackDialog to
// collect an optional issue type + details. Feedback can be re-submitted at any
// time (e.g. switching the rating) — the chosen thumb is filled to indicate the
// last selection, and a transient toast confirms each submission.
const feedbackDialogChat = ref<Chat | null>(null);

// Transient confirmation toast shown after a feedback submission (success or
// failure). Auto-dismisses; clicking it dismisses early.
const feedbackToast = ref<string | null>(null);
let feedbackToastTimer: ReturnType<typeof setTimeout> | null = null;
const showFeedbackToast = (message: string) => {
    feedbackToast.value = message;
    if (feedbackToastTimer) clearTimeout(feedbackToastTimer);
    feedbackToastTimer = setTimeout(() => {
        feedbackToast.value = null;
    }, 3000);
};
const dismissFeedbackToast = () => {
    if (feedbackToastTimer) clearTimeout(feedbackToastTimer);
    feedbackToast.value = null;
};

const showFeedback = (chat: Chat) =>
    chat.Trigger === 'AGENT' && chat.IsComplete !== false;

// The most recent chat always shows its response menu (rather than only on
// hover). Every other response keeps the hover-to-reveal behaviour.
const isLastChat = (chat: Chat) =>
    chatStore.chats[chatStore.chats.length - 1]?.RecordId === chat.RecordId;

const ratingFor = (chat: Chat) => chatStore.dialogFeedback[chat.RecordId];
// The rating currently being submitted for this chat, if any — drives the
// in-flight spinner on the matching thumb and guards against double-submits.
const submittingDirection = (chat: Chat) => chatStore.feedbackSubmitting[chat.RecordId];

const onThumbsUp = async (chat: Chat) => {
    if (submittingDirection(chat)) return;
    const ok = await chatStore.submitDialogFeedback(chat.RecordId, { isPositive: true });
    showFeedbackToast(ok ? t('feedback.toastSuccess') : t('feedback.toastError'));
};

const onThumbsDown = (chat: Chat) => {
    if (submittingDirection(chat)) return;
    feedbackDialogChat.value = chat;
};

// The dialog closes immediately on submit; the in-flight request is surfaced by
// a spinner on the thumbs-down button (the store records the negative submit
// against this chat), and a toast confirms success/failure once it resolves.
const onFeedbackSubmit = (payload: { issueType: string; text: string }) => {
    const chat = feedbackDialogChat.value;
    if (!chat) return;
    feedbackDialogChat.value = null;
    void chatStore
        .submitDialogFeedback(chat.RecordId, {
            isPositive: false,
            issueReported: payload.issueType,
            feedbackDetails: payload.text,
        })
        .then((ok) => {
            showFeedbackToast(ok ? t('feedback.toastSuccess') : t('feedback.toastError'));
        });
};

// Canceling the dialog still records a negative-feedback flag in the backend
// (dropping any issue type / details the user typed), but silently — no thumb
// highlight, lock, busy state, or error hint. The dialog closes immediately and
// the post is fire-and-forget.
const onFeedbackSkip = () => {
    const chat = feedbackDialogChat.value;
    feedbackDialogChat.value = null;
    if (chat) {
        void chatStore.submitDialogFeedback(chat.RecordId, {
            isPositive: false,
            silent: true,
        });
    }
};

// --- Copy response ------------------------------------------------------------
// RecordId of the chat whose response was just copied — drives a transient
// checkmark on that copy button. Copies the raw answer text (markdown source).
const copiedId = ref<string | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

const onCopyResponse = async (chat: Chat) => {
    try {
        await navigator.clipboard.writeText(chat.DisplayText ?? '');
        copiedId.value = chat.RecordId;
        if (copiedTimer) clearTimeout(copiedTimer);
        copiedTimer = setTimeout(() => {
            copiedId.value = null;
        }, 1500);
    } catch (err) {
        console.error('Failed to copy response:', err);
    }
};

// --- Add to findings ---------------------------------------------------------
// Hovering a response reveals an "Add to findings" action that opens the
// Add to Findings dialog for that single chat. Submitting records a finding via
// the findings store; success/failure is confirmed with the shared toast. Only
// one response can be saved at a time (the dialog is bound to one chat).
const findingsDialogChat = ref<Chat | null>(null);

// Existing narratives offered in the dialog's "file under narrative" combobox.
const narrativeOptions = computed(() =>
    narrativesStore.narratives.map((n) => ({ id: n.id, title: n.title })),
);

const onAddToFindings = (chat: Chat) => {
    findingsDialogChat.value = chat;
    // Load the matter's narratives so the combobox is populated (no-op on a
    // same-matter revisit; failures leave the combobox empty, still usable to
    // type a new narrative name).
    narrativesStore.fetchNarratives().catch(() => {});
};

const closeFindingsDialog = () => {
    findingsDialogChat.value = null;
};

const onAddFinding = async (payload: FindingSubmit) => {
    const chat = findingsDialogChat.value;
    if (!chat) return;
    const evidenceCount = referenceCountFor(chat);

    // A typed-but-new narrative is created first so the finding can link to its
    // id. If that create fails, abort rather than silently dropping the link.
    let narrativeId = payload.narrativeId;
    if (payload.newNarrativeTitle) {
        try {
            const narrative = await narrativesStore.createNarrative(payload.newNarrativeTitle);
            narrativeId = narrative.id;
        } catch {
            findingsDialogChat.value = null;
            showFeedbackToast("Couldn't create the narrative — try again");
            return;
        }
    }

    const finding = await findingsStore.createFinding(payload.description, {
        chatId: chat.RecordId,
        narrativeId,
        evidenceCount,
    });
    findingsDialogChat.value = null;
    showFeedbackToast(
        finding ? 'Saved to findings' : "Couldn't save to findings — try again",
    );
};

// --- Response selection (checkbox → action toolbar) --------------------------
// Hovering a completed agent response reveals a checkbox; checking it selects
// that response and reveals the dark action toolbar (View references / Add to
// findings). One response at a time for now — selecting another replaces the
// selection; the toolbar's ✕ (or unchecking) clears it. The "N selected" label
// is pluralized so it's ready if multi-select lands.
const selectedResponseId = ref<string | null>(null);

const isResponseSelected = (chat: Chat) => selectedResponseId.value === chat.RecordId;

const toggleResponseSelection = (chat: Chat) => {
    selectedResponseId.value = isResponseSelected(chat) ? null : chat.RecordId;
};

const clearResponseSelection = () => {
    selectedResponseId.value = null;
};

const getMethodology = (chat: Chat) => {
    if (!chat.Raw) return null;
    try {
        const raw = JSON.parse(chat.Raw as string);
        if (raw.type === 'tools_v2' && raw.methodology && raw.methodology.length > 0) {
            return raw.methodology;
        }
    } catch {
        // JSON parse error, return null
    }
    return null;
};

// --- In-flight workflow progress indicator -----------------------------------
// Map the raw DET step kind to one of the high-level "thinking" stages shown
// to the user (label + detail). Defaults to the first stage before any step
// is reported.
const thinkingStep = computed(() =>
    thinkingStepForKind(chatStore.workflowStatus?.current_step_kind),
);

// Crossfade the stage text when the step changes: fade the text block out, swap
// to the new stage while it's invisible, fade back in. `displayedStep` lags the
// live `thinkingStep` so the text never pops mid-fade. STEP_FADE_MS matches the
// CSS opacity transition on .workflow-progress-text.
const STEP_FADE_MS = 250;
const displayedStep = ref(thinkingStep.value);
const stepFading = ref(false);
let stepFadeTimer: ReturnType<typeof setTimeout> | null = null;

watch(
    () => thinkingStep.value.id,
    () => {
        if (stepFadeTimer !== null) clearTimeout(stepFadeTimer);
        stepFading.value = true;
        stepFadeTimer = setTimeout(() => {
            displayedStep.value = thinkingStep.value;
            stepFading.value = false;
            stepFadeTimer = null;
        }, STEP_FADE_MS);
    },
);

// Finer-grained, literal detail e.g. "Question 2 of 5 · query 1 of 3". Built
// from the optional progress fields the backend reports; empty when none
// apply (early planning steps, conversational replies). Surfaced on hover
// over the stage's detail line rather than shown inline.
const progressDetail = computed(() => {
    const s = chatStore.workflowStatus;
    if (!s) return '';
    const parts: string[] = [];
    if (s.total_questions && s.current_question_index) {
        parts.push(`Question ${s.current_question_index} of ${s.total_questions}`);
    }
    if (s.subroutines_total) {
        const done = s.subroutines_completed ?? 0;
        const current = Math.min(done + 1, s.subroutines_total);
        parts.push(`query ${current} of ${s.subroutines_total}`);
    }
    return parts.join(' · ');
});
</script>

<template>
    <div ref="chatContainer" class="chat-window">
        <div v-if="chatStore.loading" class="chat-loading">
            <svg
                width="22"
                height="10"
                viewBox="0 0 22 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M5 2C3.34315 2 2 3.3431 2 5C2 6.6569 3.34315 8 5 8C6.65685 8 8 6.6569 8 5C8 4.3629 7.8025 3.7748 7.46538 3.29C6.92183 2.50831 6.02019 2 5 2ZM0 5C0 2.23858 2.23858 0 5 0C6.21392 0 7.32661 0.43307 8.19203 1.1515C8.91366 0.44003 9.905 0 11 0C12.095 0 13.0863 0.44003 13.808 1.1515C14.6734 0.43307 15.7861 0 17 0C19.7614 0 22 2.23858 22 5C22 7.7614 19.7614 10 17 10C14.2386 10 12 7.7614 12 5C12 4.1835 12.1964 3.411 12.5445 2.72905C12.177 2.28296 11.6209 2 11 2C10.3791 2 9.823 2.28296 9.4555 2.72905C9.8036 3.411 10 4.1835 10 5C10 7.7614 7.76142 10 5 10C2.23858 10 0 7.7614 0 5ZM17 2C15.9798 2 15.0782 2.50831 14.5346 3.29C14.1975 3.7748 14 4.3629 14 5C14 6.6569 15.3431 8 17 8C18.6569 8 20 6.6569 20 5C20 3.3431 18.6569 2 17 2Z"
                    fill="#D01530"
                />
            </svg>
            <div class="loading-dots"><span></span><span></span><span></span></div>
        </div>
        <div v-else-if="chatStore.error" class="chat-error">{{ chatStore.error }}</div>

        <div v-else>
            <div class="chat-messages">
                <div
                    v-for="chat in chatStore.chats"
                    :key="chat.RecordId"
                    class="chat-message-container"
                    :class="{
                        'chat-message-container-user': chat.Trigger === 'USER',
                        'chat-message-container-agent': chat.Trigger === 'AGENT',
                        'chat-message-container-dimmed': isChatDimmed(chat),
                    }"
                >
                    <div
                        class="chat-message"
                        :class="{
                            'chat-message-user': chat.Trigger === 'USER',
                            'chat-message-agent': chat.Trigger === 'AGENT',
                            'chat-message-selected': isChatSelected(chat),
                        }"
                        :title="
                            (chat as any).IsComplete === false
                                ? undefined
                                : new Date(chat.Timestamp).toLocaleString()
                        "
                        @contextmenu="onChatContextMenu(chat, $event)"
                    >
                        <div
                            v-if="(chat as any).IsComplete === false"
                            class="workflow-progress"
                        >
                            <div class="workflow-progress-star" aria-hidden="true"></div>
                            <div
                                class="workflow-progress-text"
                                :class="{ 'is-fading': stepFading }"
                            >
                                <div class="workflow-progress-fun">
                                    {{ displayedStep.label
                                    }}<span class="whimsy-ellipsis" aria-hidden="true"
                                        ><span>.</span><span>.</span><span>.</span><span>.</span
                                        ><span>.</span><span>.</span></span
                                    >
                                </div>
                                <div
                                    class="workflow-progress-real"
                                    :title="progressDetail || undefined"
                                >
                                    {{ displayedStep.detail }}
                                </div>
                            </div>
                        </div>
                        <!-- eslint-disable-next-line vue/no-v-html -- renderMarkdown sanitizes via DOMPurify -->
                        <div v-else class="chat-text" v-html="renderMarkdown(chat.DisplayText)"></div>
                    </div>
                    <button
                        v-if="showFeedback(chat)"
                        type="button"
                        class="chat-select-checkbox"
                        :class="{ 'chat-select-checkbox-checked': isResponseSelected(chat) }"
                        :aria-label="t('chat.selectResponse')"
                        :aria-pressed="isResponseSelected(chat)"
                        @click="toggleResponseSelection(chat)"
                    >
                        <span v-if="isResponseSelected(chat)" class="material-icons">check</span>
                    </button>
                    <SearchMethodologySection
                        v-if="
                            chat.Trigger === 'AGENT' &&
                            (chat as any).IsComplete !== false &&
                            getMethodology(chat)
                        "
                        :methodology="getMethodology(chat)"
                    />
                    <div
                        v-if="showFeedback(chat)"
                        class="chat-feedback"
                        :class="{
                            'chat-feedback-rated': !!ratingFor(chat),
                            'chat-feedback-submitting': !!submittingDirection(chat),
                            'chat-feedback-latest': isLastChat(chat),
                        }"
                    >
                        <button
                            type="button"
                            class="chat-feedback-btn"
                            :title="copiedId === chat.RecordId ? t('chat.copied') : t('chat.copyResponse')"
                            :aria-label="copiedId === chat.RecordId ? t('chat.copied') : t('chat.copyResponse')"
                            @click="onCopyResponse(chat)"
                        >
                            <span class="material-icons-outlined">{{
                                copiedId === chat.RecordId ? 'check' : 'content_copy'
                            }}</span>
                        </button>
                        <button
                            type="button"
                            class="chat-feedback-btn"
                            :class="{ 'chat-feedback-btn-active': ratingFor(chat) === 'positive' }"
                            :title="t('chat.goodResponse')"
                            :aria-label="t('chat.goodResponse')"
                            :disabled="!!submittingDirection(chat)"
                            @click="onThumbsUp(chat)"
                        >
                            <span
                                v-if="submittingDirection(chat) === 'positive'"
                                class="chat-feedback-spinner"
                                aria-hidden="true"
                            ></span>
                            <span
                                v-else
                                :class="
                                    ratingFor(chat) === 'positive'
                                        ? 'material-icons'
                                        : 'material-icons-outlined'
                                "
                                >thumb_up</span
                            >
                        </button>
                        <button
                            type="button"
                            class="chat-feedback-btn"
                            :class="{ 'chat-feedback-btn-active': ratingFor(chat) === 'negative' }"
                            :title="t('chat.badResponse')"
                            :aria-label="t('chat.badResponse')"
                            :disabled="!!submittingDirection(chat)"
                            @click="onThumbsDown(chat)"
                        >
                            <span
                                v-if="submittingDirection(chat) === 'negative'"
                                class="chat-feedback-spinner"
                                aria-hidden="true"
                            ></span>
                            <span
                                v-else
                                :class="
                                    ratingFor(chat) === 'negative'
                                        ? 'material-icons'
                                        : 'material-icons-outlined'
                                "
                                >thumb_down</span
                            >
                        </button>
                    </div>
                    <div v-if="isResponseSelected(chat)" class="chat-select-toolbar">
                        <span class="chat-select-toolbar-count">{{
                            t('chat.nSelected', 1)
                        }}</span>
                        <button
                            type="button"
                            class="chat-select-toolbar-btn"
                            @click="viewDetails(chat)"
                        >
                            <span class="material-icons-outlined">folder_open</span>
                            <span>{{ t('chat.viewReferences') }}</span>
                        </button>
                        <button
                            type="button"
                            class="chat-select-toolbar-btn"
                            @click="onAddToFindings(chat)"
                        >
                            <span class="material-icons-outlined">bookmark_border</span>
                            <span>{{ t('chat.addToFindings') }}</span>
                        </button>
                        <button
                            type="button"
                            class="chat-select-toolbar-close"
                            :aria-label="t('common.close')"
                            @click="clearResponseSelection"
                        >
                            <span class="material-icons-outlined">close</span>
                        </button>
                    </div>
                </div>

                <div v-if="isEmptyState" class="empty-chat-container">
                    <h2 class="empty-chat-heading">{{ t('chat.emptyStateHeading') }}</h2>
                    <ChatInput variant="hero" />
                </div>
            </div>
        </div>
        <ContextMenu
            :position="chatContextMenu ? { x: chatContextMenu.x, y: chatContextMenu.y } : null"
            :items="chatMenuItems"
            @close="closeChatContextMenu"
        />
        <FeedbackDialog
            :visible="feedbackDialogChat !== null"
            @submit="onFeedbackSubmit"
            @skip="onFeedbackSkip"
        />
        <AddToFindingsDialog
            :visible="findingsDialogChat !== null"
            :evidence-count="findingsDialogChat ? referenceCountFor(findingsDialogChat) : undefined"
            :narratives="narrativeOptions"
            :submitting="findingsStore.creating"
            @submit="onAddFinding"
            @close="closeFindingsDialog"
        />
        <div v-if="feedbackToast" class="chat-feedback-toast" @click="dismissFeedbackToast">
            {{ feedbackToast }}
        </div>
    </div>
</template>
